import Image from 'next/image';
import Link from 'next/link';

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/fr';
import { BlogArticle } from '@/settings/blogs/types';
import i18n from '@/settings/i18n';
import { formatStoreDate } from '@/lib/intl-format';

dayjs.extend(utc);

const t = i18n.init('blogs');

// French writes the day before the month with no ordinal ("5 janv. 2024").
// publishedAt is a calendar date at midnight UTC, so format it in UTC or
// timezones behind UTC show the day before.
const formatBlogDate = (date: string) => {
  const locale = i18n.getLocale();

  const published = dayjs.utc(date);

  return (
    formatStoreDate(published, 'order') ??
    published
      .locale(locale)
      .format(locale === 'fr' ? 'D MMM YYYY' : 'MMM Do YYYY')
  );
};

export default function Sidebar(props: { articles: BlogArticle[] }) {
  dayjs.extend(advancedFormat);
  const sideBarArticles =
    props.articles.length > 5
      ? props.articles.slice(2, 5)
      : props.articles.slice(0, 3);

  return (
    <div className="pl-7">
      <h2 className="mb-5 text-lg font-bold text-gray-700 md:text-2xl">
        {t`Popular posts`}
      </h2>
      <ul className="list-none space-y-6">
        {sideBarArticles.map((a, i) => (
          <li className="flex" key={a.handle}>
            {a?.image && (
              <Link href={`/blogs/${a.handle}`}>
                <div className="relative flex aspect-video w-[100px]">
                  <span className="absolute -left-4 top-[50%] z-10 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-blue-500 text-sm text-white">
                    {i + 1}
                  </span>
                  <Image
                    src={a.image}
                    alt={t`Blog image`}
                    className="h-full w-full rounded-lg object-cover duration-300 ease-in-out hover:scale-105"
                    width={100}
                    height={80}
                  />
                </div>
              </Link>
            )}

            <div className="pl-5">
              <Link href={`/blogs/${a.handle}`}>
                <h4 className="text-sm font-semibold leading-tight text-gray-700 duration-150 ease-in-out hover:text-blue-500">
                  {a.title}
                </h4>
              </Link>
              <p className="text-sm text-gray-700/75">
                {a.tags.length > 0 && (
                  <>
                    <span>{a.tags[0]}</span>
                    <span className="mx-2.5 inline-block h-1.5 w-1.5 rounded-full bg-gray-700"></span>
                  </>
                )}
                <span>{formatBlogDate(a.publishedAt)}</span>
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
